import PropTypes from 'prop-types';
import React, { Component, useContext, useEffect, useMemo, useRef } from 'react';
import { Router } from 'react-router-dom';

import i18n from 'i18next';
import isJSON from 'is-json';
import isMobile from 'ismobilejs';
import jwtDecode from 'jwt-decode';
import { cloneDeep, get, isEmpty, merge, mergeWith, set } from 'lodash';
import moment from 'moment';
import 'normalize.css';
import GoogleFontLoader from 'react-google-font-loader';
import { initReactI18next, useTranslation } from 'react-i18next';

import * as SDK from '@ezbobdev/ui-sdk';

import defaultConfig from './config/config';
import { mapMuiFontsToGoogleFonts } from './config/materialTheme';
import { interpolateTheme } from './config/theme';
import { CaseActions } from './context/CaseActions';
import { CaseValidationActions } from './context/CaseValidationActions';
import { EventsActions } from './context/EventsActions';
import { ExternalServicesActions } from './context/ExternalServicesActions';
import { SdkActions } from './context/SdkActions';
import { SettingsActions } from './context/SettingsActions';
import { SystemSettingsActions } from './context/SystemSettingsActions';
import { UserStateActions } from './context/UserStateActions';
import { ViewActions } from './context/ViewActions';
import systemConfig from './systemConfig';
import {
  checkIsIframe,
  checkIsWebView,
  defineFormattingOptions,
  generateUuid,
  history,
  parseURLParams,
} from './utils/commons';
import { FLOW_TYPE, SYSTEM_EVENTS, UI_FW_MODES, URL_PARAMS } from './utils/constants';
import './utils/fa-icons';
import './utils/polyfills';
import { initTranslations, setMomentLocale } from './utils/translations';

class UIFramework extends Component {
  constructor(props) {
    super(props);

    const theme = merge(get(defaultConfig, 'theme', {}), get(props.config, 'theme', {}));

    this.state = {
      initialized: false,
      userData: {},
      config: {
        serverConfigLoaded: false,
        ...props.config,
        theme,
      },
      systemConfig,
      userState: {
        mode: props.mode || UI_FW_MODES.enterprise,
        demoMode: props.demoMode,
        authorities: ['ROLE_ANONYMOUS'],
        validUiVersion: true,
      },
      actions: {},
      sdkActions: {},
      sdkResponses: {},
      view: {
        modal: {},
        showPopupLoader: false,
        popupLoaderText: null,
        scrollMode: false,
        alerts: [],
        modalContent: false,
        routeSettings: {
          excludeModules: [],
        },
      },
    };

    this.setActions();
    this.initLocalAndSessionStorage();
    this.initFlowFromUrl();
    this.initSdkOptions();
    this.state.actions.caseValidation.setCustomValidators();
    this.setUserFromQueryString();
  }

  initSdkOptions() {
    const { getUserConfigProp } = this.state.actions.settings;
    const urlParams = parseURLParams();

    SDK.Tools.setOptions({
      demoMode: this.state.userState.demoMode,
      logoutURL: getUserConfigProp('routing.logoutUrl') ?? getUserConfigProp('pageRoutes.customerWizard.url'),
    });

    if (checkIsWebView()) {
      const deviceId = urlParams.DeviceID || urlParams.deviceID || urlParams.deviceId || urlParams.deviceid;

      deviceId && SDK.Tools.setOption('deviceId', deviceId);
    }
  }

  setActions() {
    this.state.actions = {
      case: {},
      caseValidation: {},
      settings: {},
      systemSettings: {},
      userState: {},
      view: {},
      externalServices: {},
      events: {},
    };

    Object.keys(CaseActions).forEach((action) => (this.state.actions.case[action] = CaseActions[action].bind(this)));
    Object.keys(CaseValidationActions).forEach(
      (action) => (this.state.actions.caseValidation[action] = CaseValidationActions[action].bind(this)),
    );
    Object.keys(SettingsActions).forEach(
      (action) => (this.state.actions.settings[action] = SettingsActions[action].bind(this)),
    );
    Object.keys(SystemSettingsActions).forEach(
      (action) => (this.state.actions.systemSettings[action] = SystemSettingsActions[action].bind(this)),
    );
    Object.keys(UserStateActions).forEach(
      (action) => (this.state.actions.userState[action] = UserStateActions[action].bind(this)),
    );
    Object.keys(ViewActions).forEach((action) => (this.state.actions.view[action] = ViewActions[action].bind(this)));

    Object.keys(EventsActions).forEach(
      (action) => (this.state.actions.events[action] = EventsActions[action].bind(this)),
    );

    Object.keys(ExternalServicesActions).forEach((service) => {
      Object.keys(ExternalServicesActions[service]).forEach((method) => {
        set(
          this.state.actions.externalServices,
          `${service}.${method}`,
          typeof ExternalServicesActions[service][method] === 'function'
            ? ExternalServicesActions[service][method].bind(this)
            : ExternalServicesActions[service][method],
        );
      });
    });

    Object.keys(SdkActions).forEach((service) => {
      Object.keys(SdkActions[service]).forEach((method) => {
        set(
          this.state.sdkActions,
          `${service}.${method}`,
          typeof SdkActions[service][method] === 'function'
            ? SdkActions[service][method].bind(this)
            : SdkActions[service][method],
        );
      });
    });

    window.ezbob = this.state;
    window.SDK = SDK;
  }

  initLocalAndSessionStorage() {
    const URLParams = parseURLParams();

    if (!SDK.Tools.storage.getItem('sessionId')) {
      SDK.Tools.storage.setItem('sessionId', generateUuid());
    }

    SDK.Tools.storage.setItem('sessionTime', moment().format('X'));

    Object.keys(sessionStorage).forEach((key) => {
      if (key !== 'sessionId' && key !== 'sessionTime' && key !== 'appToken') {
        SDK.Tools.setOption(key, sessionStorage.getItem(key));
      }
    });

    Object.keys(localStorage).forEach((key) => {
      if (key !== 'sessionId' && key !== 'sessionTime' && key !== 'appToken') {
        SDK.Tools.setOption(key, localStorage.getItem(key));
      }
    });

    Object.keys(URLParams).forEach((key) => {
      if (URL_PARAMS.includes(key)) {
        SDK.Tools.setOption(key, URLParams[key]);
      }
    });
  }

  initFlowFromUrl() {
    const { flow } = parseURLParams();

    SDK.Tools.setOption('flow', flow?.toUpperCase() || FLOW_TYPE.onboarding);
  }

  initLocales() {
    const { config } = this.state;
    const formattingOptions = defineFormattingOptions({
      locale: i18n.language,
      currency: config.locals.currency,
      currencySign: config.locals.currencySign,
      useCustomCurrencyFormat: config.locals.useCustomCurrencyFormat,
    });

    document.documentElement.lang = i18n.language;

    this.setState(
      (prevState) => ({
        ...prevState,
        config: {
          ...prevState.config,
          locals: {
            ...prevState.config.locals,
            ...formattingOptions,
          },
        },
      }),
      () => (i18n.options.interpolation.defaultVariables = this.state.config),
    );
  }

  async initialize(shouldShowTabWarning) {
    const { actions, userState } = this.state;

    await this.initTranslations();

    this.initLocales();

    if ([UI_FW_MODES.express, UI_FW_MODES.backoffice, UI_FW_MODES.userPortal].includes(userState.mode)) {
      this.initMaterialTheme();
    } else {
      this.initTheme();
    }

    this.setFavicon();
    this.preloadCriticalAssets();
    this.devTools();

    if (!checkIsIframe()) {
      this.inactiveUser();
    }

    const linkBehaviorOverrides = actions.settings.getUserConfigProp('system.linkBehaviorOverrides');

    if (!isEmpty(linkBehaviorOverrides)) {
      const isWebview = checkIsWebView();

      window.addEventListener(
        'click',
        handleLinkClicks(actions.view.toggleModal, actions.events.emit, linkBehaviorOverrides, isWebview),
      );
    }

    if (shouldShowTabWarning) {
      actions.view.setAlerts([{ message: i18n.t('globals.parallelSessionWarning'), type: 'info' }]);
    }

    this.setState((prevState) => {
      return { ...prevState, initialized: true };
    });
  }

  overrideProductByTenantData(paths, source) {
    const targetPath = 'tenantData';
    const replaced = cloneDeep(source);

    paths.forEach((path) => {
      replaced[path] = cloneDeep(source[targetPath][path]);
      delete replaced[targetPath][path];
    });

    return replaced;
  }

  async initConfigurationsFromServer() {
    const { sdkActions } = this.state;

    try {
      const configurationServerData = await sdkActions.configuration.getAllConfigurations();

      if (get(configurationServerData, 'data.data')) {
        let mergedConfig = mergeWith(
          { ...this.state.config, serverConfigLoaded: true },
          configurationServerData.data.data,
          (objValue, srcValue) => {
            if (Array.isArray(objValue) && Array.isArray(srcValue)) {
              return srcValue;
            }
          },
        );

        if (mergedConfig.tenantData.useTenantBranding) {
          mergedConfig = this.overrideProductByTenantData(['theme', 'materialTheme', 'assets'], mergedConfig);
        }

        return mergedConfig;
      }
    } catch (e) {
      console.error(e);
    }
  }

  transformRoutesForProductPath(config, productPath) {
    if (!config?.pageRoutes) return config;

    const transformed = { ...config, pageRoutes: { ...config.pageRoutes } };

    Object.keys(transformed.pageRoutes).forEach((routeKey) => {
      const route = transformed.pageRoutes[routeKey];
      if (route?.url?.startsWith('/customer/')) {
        transformed.pageRoutes[routeKey] = {
          ...route,
          url: route.url.replace('/customer/', `/${productPath}/`),
        };
      }

      if (route?.pageActions) {
        const transformAction = (action) => {
          if (action?.url?.startsWith('/customer/')) {
            return { ...action, url: action.url.replace('/customer/', `/${productPath}/`) };
          }
          return action;
        };

        const transformedActions = Array.isArray(route.pageActions)
          ? route.pageActions.map(transformAction)
          : Object.keys(route.pageActions).reduce((acc, actionKey) => {
              acc[actionKey] = transformAction(route.pageActions[actionKey]);
              return acc;
            }, {});

        transformed.pageRoutes[routeKey] = {
          ...transformed.pageRoutes[routeKey],
          pageActions: transformedActions,
        };
      }
    });

    if (transformed.system?.inactiveLogout?.redirectUrl?.startsWith('/customer/')) {
      transformed.system = {
        ...transformed.system,
        inactiveLogout: {
          ...transformed.system.inactiveLogout,
          redirectUrl: transformed.system.inactiveLogout.redirectUrl.replace('/customer/', `/${productPath}/`),
        },
      };
    }

    if (transformed.routing?.logoutUrl?.startsWith('/customer/')) {
      transformed.routing = {
        ...transformed.routing,
        logoutUrl: transformed.routing.logoutUrl.replace('/customer/', `/${productPath}/`),
      };
    }

    return transformed;
  }

  mergeChannelConfigs(productConfig, channelName) {
    const channelConfig = cloneDeep(productConfig?.channels?.[channelName]);
    if (!channelConfig) console.error('Missing channel configuration');
    delete productConfig.channels;

    const mergedChannelConfig = mergeWith(productConfig, channelConfig, (objValue, srcValue) => {
      if (Array.isArray(objValue) && Array.isArray(srcValue)) {
        return srcValue;
      }
    });
    return mergedChannelConfig;
  }

  /**
   * DL-4500: Re-initializes the app config with a different channel.
   * Used when the login JWT contains a different channel than the one
   * loaded at app startup (e.g. user has an active case on another channel).
   *
   * The config backend filters response data by the channel in the JWT.
   * - Cookie auth: the login cookie already carries the new channel, so
   *   re-fetching configs returns the correct channel data automatically.
   * - Bearer auth: the app token still has the old channel, so we must
   *   re-issue it with the new channel before fetching configs.
   */
  async switchChannel(newChannel) {
    try {
      // Note: we only update 'channel' (the resolved name from the JWT), not 'channelId'
      // (the URL slug used by getAppToken). The backend maps channelId -> channel name via
      // channels-map config, and the login JWT only carries the name, not the slug.
      // With cookie auth, getAppToken is not re-triggered post-login, so the stale
      // channelId is harmless. For bearer auth, getAppToken would need to be called
      // with the channelId (URL slug, e.g. "c4poqltt"), not the channel name from
      // the JWT (e.g. "greenlend"). Since the JWT doesn't carry the slug, bearer
      // auth channel switching is not supported — but all current deployments use
      // cookie auth (withCredentials: true).
      SDK.Tools.setOption('channel', newChannel);

      // Skip for cookie auth — the login cookie already has the correct channel,
      // and re-issuing the app token would overwrite the login cookie.

      let mergedConfig = await this.initConfigurationsFromServer();
      if (!mergedConfig) return;

      mergedConfig = this.mergeChannelConfigs(mergedConfig, newChannel);

      const productPath = SDK.Tools.getAuthConfig('productPath');
      if (productPath && productPath !== 'customer') {
        mergedConfig = this.transformRoutesForProductPath(mergedConfig, productPath);
      }

      this.setState(
        (prevState) => ({ ...prevState, config: mergedConfig }),
        () => this.initialize(),
      );
    } catch (e) {
      console.error('switchChannel failed', e);
    }
  }

  inactiveUser() {
    const inactiveTimeoutFunc = () => {
      if (this.accessTokenTimeoutRef) clearTimeout(this.accessTokenTimeoutRef);

      const { minutes = 10, redirectUrl } = this.state.actions.settings.getUserConfigProp('system.inactiveLogout');
      isMobile().any && localStorage.setItem('lastActionTime', Date.now().toString());
      this.accessTokenTimeoutRef = setTimeout(async () => {
        if (this.state.actions.userState.isLoggedIn()) {
          await this.state.sdkActions.users.logout(redirectUrl);
          this.state.actions.userState.resetUserState();
          console.warn('Should logout now after no activity');
        }
      }, moment.duration({ minutes }).asMilliseconds());
    };

    inactiveTimeoutFunc();

    window.addEventListener('click', inactiveTimeoutFunc);
    window.addEventListener('keyup', inactiveTimeoutFunc);
  }

  /**
   * Handles when the user returns to the app (tab becomes visible)
   * Checks if the user has been inactive for too long and logs them out if necessary
   * Only runs on mobile devices where lastActionTime is set
   *
   * @async
   * @returns {Promise<void>}
   */
  handleAppResume = async () => {
    if (!document.hidden) {
      const lastActionTime = localStorage.getItem('lastActionTime');

      if (isMobile().any && lastActionTime && this.state.actions.userState.isLoggedIn()) {
        this.state.actions.view.togglePopupLoader(true);

        try {
          const currentTime = Date.now();
          const inactivityTime = currentTime - parseInt(lastActionTime);
          const { minutes = 10, redirectUrl } = this.state.actions.settings.getUserConfigProp('system.inactiveLogout');
          const timeoutMs = moment.duration({ minutes }).asMilliseconds();

          // Check if user has been inactive longer than the configured timeout
          if (inactivityTime > timeoutMs) {
            console.warn(`User inactive for ${Math.round(inactivityTime / 60000)} minutes, logging out`);
            await this.state.sdkActions.users.logout(redirectUrl);
          }
        } catch (error) {
          console.error('Error handling visibility change:', error);
        } finally {
          this.state.actions.view.togglePopupLoader(false);
        }
      }
    }
  };

  componentDidMount() {
    this.initializeMount();
  }

  async initializeMount() {
    const { actions, sdkActions, userState } = this.state;

    if (!checkIsIframe()) {
      this.inactiveUser();
    }

    const { product } = parseURLParams();

    if (product) {
      SDK.Tools.setOption('product', product);
    }

    // Check if there is a logged in user (in case of a refresh or opening in iframe)
    const accessToken = SDK.Tools.storage.getItem('accessToken') || SDK.Tools.getOption('accessToken');

    // If there is a saved access token in storage or memory
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);

      let isTokenValid = !!decodedToken;

      // For BO and UP check if token is expired and don't save it's data to storage, so AuthenticatedRoute/AuthorizedRoute will redirect to login
      if (isTokenValid && (userState.mode === UI_FW_MODES.backoffice || userState.mode === UI_FW_MODES.userPortal)) {
        const expirationDate = moment.unix(decodedToken.exp);
        isTokenValid = moment().isBefore(expirationDate);
      }

      isTokenValid && this.state.sdkActions.oauth.mapUserDetailsToSDKOptions(decodedToken);

      // Else check for saved userData (mainly for the AuthenticatedRoute)
    } else if (SDK.Tools.storage.getItem('userData')) {
      const data = SDK.Tools.storage.getItem('userData');

      const userData = isJSON(data) ? JSON.parse(data) : data;

      userData && this.state.sdkActions.oauth.mapUserDetailsToSDKOptions({ userData });
    }

    try {
      const { shouldShowTabWarning } = await sdkActions.system.initializeApp();

      if (SDK.Tools.getAuthConfig('withCredentials')) {
        this.activeTabInterval = setInterval(() => {
          localStorage.setItem('activeTabTime', moment());
        }, 5000);
      }

      if (userState.demoMode) {
        this.initialize(shouldShowTabWarning);

        window.addEventListener('beforeunload', actions.view.handleBeforeUnload);
      } else {
        let mergedConfig = await this.initConfigurationsFromServer();

        const channel = SDK.Tools.getOption('channel');

        if (channel) {
          mergedConfig = this.mergeChannelConfigs(mergedConfig, channel);
        }

        const productPath = SDK.Tools.getAuthConfig('productPath');
        if (productPath && productPath !== 'customer') {
          mergedConfig = this.transformRoutesForProductPath(mergedConfig, productPath);
        }

        if (get(mergedConfig, 'system.warnOnAppLeave', true)) {
          window.addEventListener('beforeunload', actions.view.handleBeforeUnload);
        }

        if (mergedConfig.externalScripts) {
          loadExternalScripts(mergedConfig.externalScripts);
        }

        if (get(mergedConfig, 'system.enableDeviceFraudCheck', false)) {
          import('./assets/js/loader_general5');
        }

        document.addEventListener('visibilitychange', this.handleAppResume);

        this.setState(
          (prevState) => ({
            ...prevState,
            config: mergedConfig,
          }),
          () => this.initialize(shouldShowTabWarning),
        );
      }
    } catch (e) {
      console.error(e);
    }
  }

  async initTranslations() {
    const { config } = this.state;
    const { languageList, defaultLanguage, fallbackLanguage, supportedLanguages, autoDetectOptions } = get(
      config,
      'languages',
    );

    const i18nOptions = {
      resources: languageList,
      fallbackLng: fallbackLanguage || get(defaultConfig, 'languages.defaultLanguage'),
      parseMissingKeyHandler: (path, defaultValue) => {
        return defaultValue || (get(window, 'location.hostname') === 'local.ezbob.com' ? path : '');
      },
      interpolation: {
        escapeValue: false,
        defaultVariables: config,
        format: function (value, format) {
          if (format && format.includes('validationRule:')) {
            const rule = format.replace('validationRule:', '');
            const val = value
              .split('|')
              .reduce((acc, curr) => (curr.includes(rule) ? curr.replace(`${rule}:`, '') : acc), '');

            return !isNaN(val) ? parseInt(val).toLocaleString() : val;
          }

          if (format === 'number' && !isNaN(value)) {
            return parseInt(value).toLocaleString();
          }

          return value;
        },
      },
    };

    if (supportedLanguages) {
      i18nOptions.supportedLngs = supportedLanguages.filter(({ enabled }) => enabled).map(({ langKey }) => langKey);
    }

    if (autoDetectOptions) {
      try {
        const LanguageDetector = await import('i18next-browser-languagedetector');

        i18n.use(LanguageDetector.default);
        i18nOptions.detection = autoDetectOptions;
      } catch (e) {
        i18nOptions.lng = defaultLanguage;
        // TODO: useLogger after converting UIFramework to functional component
        console.error(e);
      }
    } else {
      i18nOptions.lng = defaultLanguage;
    }

    initTranslations(i18n.t.bind(i18n));
    i18n.use(initReactI18next);
    i18n.init(i18nOptions);

    this.languageDirection = i18n.dir();
    document.body.dir = this.languageDirection;

    await setMomentLocale(i18n.language);

    i18n.on('languageChanged', async function (lng) {
      await setMomentLocale(lng);
    });
  }

  devTools() {
    if (get(this.state, 'config.system.isProduction')) {
      window.console.log = function () {};
      window.console.error = function () {};
      window.console.info = function () {};

      delete window.ezbob;
      delete window.SDK;
    }
  }

  initCampaignsCookies() {
    const parsedQueryString = parseURLParams();

    if (!parsedQueryString['utm_campaign']) return;

    let parsedLocalStorageCampaign = JSON.parse(localStorage.getItem('attributions')) || [];

    if (
      parsedLocalStorageCampaign[0] &&
      Number(moment.unix(parsedLocalStorageCampaign[0].timestamp).add(6, 'months').format('X')) <
        Number(moment().format('X'))
    ) {
      localStorage.removeItem('attributions');
      parsedLocalStorageCampaign = [];
    }

    const currentCampaign = {
      utmCampaign: parsedQueryString['utm_campaign'],
      utmSource: parsedQueryString['utm_source'],
      utmMedium: parsedQueryString['utm_medium'],
      utmTerm: parsedQueryString['utm_term'],
      utmContent: parsedQueryString['utm_content'],
      timestamp: Number(moment().format('X')),
    };

    if (!parsedLocalStorageCampaign.length) {
      parsedLocalStorageCampaign.push(currentCampaign);
    } else {
      parsedLocalStorageCampaign[1] = currentCampaign;
    }

    localStorage.setItem('attributions', JSON.stringify(parsedLocalStorageCampaign));
  }

  setUserFromQueryString() {
    const parsedQueryString = parseURLParams();

    try {
      const accessToken =
        parsedQueryString['token'] || parsedQueryString['access_token'] || parsedQueryString['accessToken'];

      if (accessToken) {
        SDK.Tools.setOption('accessToken', accessToken);

        const decodedToken = jwtDecode(accessToken);
        if (decodedToken.authorities) {
          this.state.userState.authorities = decodedToken.authorities;
        }
      }

      const caseId = parsedQueryString.caseId || parsedQueryString.caseid || parsedQueryString.caseUuid;
      if (caseId) {
        SDK.Tools.setOption('caseId', caseId);
      }

      const journeyId = parsedQueryString.journeyId;
      if (journeyId) {
        SDK.Tools.setOption('journeyUuid', journeyId);
      }

      const externalId = parsedQueryString.externalId;
      if (externalId) {
        SDK.Tools.setOption('externalId', externalId);
      }

      return Promise.resolve(parsedQueryString);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  setFavicon() {
    let link = document.querySelector("link[rel~='icon']");
    const faviconUrl = get(this.state.config, 'theme.favicon.url') || get(this.state.config, 'assets.favicon.src');

    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }

    if (faviconUrl) {
      link.href = faviconUrl;
    }
  }

  preloadCriticalAssets() {
    const config = this.state.config;
    const head = document.getElementsByTagName('head')[0];

    const alreadyPreloaded = new Set(
      Array.from(document.querySelectorAll('link[rel="preload"][as="image"]')).map((el) => el.href),
    );

    const emitPreload = (url) => {
      if (url && typeof url === 'string' && !alreadyPreloaded.has(url)) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        head.appendChild(link);
        alreadyPreloaded.add(url);
      }
    };

    const assets = get(config, 'assets');
    if (assets && typeof assets === 'object') {
      Object.values(assets).forEach((asset) => {
        if (asset && typeof asset === 'object') {
          emitPreload(asset.src);
        }
      });
    }

    const customSteps = get(config, 'custom');
    if (customSteps && typeof customSteps === 'object') {
      Object.values(customSteps).forEach((stepConfig) => {
        if (stepConfig && typeof stepConfig === 'object') {
          emitPreload(get(stepConfig, 'icon.src'));
        }
      });
    }
  }

  initTheme() {
    const theme = interpolateTheme(this.state.config.theme);
    this.setState((prevState) => ({
      ...prevState,
      config: { ...prevState.config, theme },
    }));
  }

  initMaterialTheme() {
    const defaultMaterialTheme = get(defaultConfig, 'materialTheme', {});
    const materialTheme = get(this.state.config, 'materialTheme', {});
    const mergedTheme = merge(defaultMaterialTheme, materialTheme);

    mergedTheme.direction = this.languageDirection;
    set(mergedTheme, 'typography.fontFamily', `${get(mergedTheme, 'typography.fontFamily', '')}, sans-serif`);

    this.setState((prevState) => ({
      ...prevState,
      config: { ...prevState.config, materialTheme: mergedTheme },
    }));
  }

  componentWillUnmount() {
    if (this.activeTabInterval) {
      clearInterval(this.activeTabInterval);
    }

    if (this.handleAppResume) {
      document.removeEventListener('visibilitychange', this.handleAppResume);
    }
  }

  render() {
    if (!this.state.userState.demoMode && !this.state.initialized) return null;

    return (
      <>
        <GoogleFonts fonts={get(this.state.config, 'materialTheme.typography.fontFamily')} />

        <MainContext.Provider value={this.state}>
          <Router history={history}>
            <Maintenance />
            <RecaptchaVersionTwo />

            <div className="UI-Framework">{this.props.children}</div>
          </Router>
        </MainContext.Provider>
      </>
    );
  }
}

function GoogleFonts(props) {
  const fonts = useMemo(() => mapMuiFontsToGoogleFonts(props.fonts), [props.fonts]);

  return <GoogleFontLoader fonts={fonts} display="swap" />;
}

function Maintenance() {
  const { t } = useTranslation();
  const { sdkActions, actions } = useContext(MainContext);

  useEffect(() => {
    const fetchMaintenanceData = async () => {
      const currentTime = moment();

      const maintenanceData = await sdkActions.system.getMaintenanceData();

      const { startDate, endDate, advanceTime } = maintenanceData ?? {};
      const { maintenanceDateFormat } = actions.settings.getUserConfigProp('locals');

      const isInMaintenance = currentTime.isBetween(moment(startDate), moment(endDate));

      if (isInMaintenance) return;

      if (startDate && advanceTime) {
        const maintenanceStartTime = moment(startDate);
        const maintenanceEndTime = moment(endDate);
        const notificationTime = moment(advanceTime);

        if (currentTime.isBetween(notificationTime, maintenanceStartTime)) {
          actions.view.setAlerts([
            {
              message: t(`globals.maintenanceAnnounceText`, {
                start: maintenanceStartTime.format(maintenanceDateFormat),
                end: maintenanceEndTime.format(maintenanceDateFormat),
              }),
              type: 'info',
            },
          ]);
        }
      }
    };
    fetchMaintenanceData();
  }, [actions, sdkActions, t]);
}

function RecaptchaVersionTwo() {
  const { actions } = useContext(MainContext);

  const containerRef = useRef(null);

  const { enabled, sitekey } = actions.settings.getUserConfigProp('externalServices.googleRecaptcha.version2') ?? {};

  useEffect(() => {
    const getRecaptcha = async () => {
      return new Promise((resolve) => {
        const tick = () => {
          window.grecaptcha?.enterprise?.ready
            ? window.grecaptcha.enterprise.ready(() => resolve(window.grecaptcha.enterprise))
            : setTimeout(tick, 100);
        };

        tick();
      });
    };

    const initiateRecaptcha = async () => {
      const containerElement = containerRef.current;

      const recaptcha = await getRecaptcha();

      const widgetId = recaptcha.render(containerElement, {
        sitekey,
        size: 'invisible',
        callback: (token) => {
          const resolve = SDK.Tools.getOption('recaptcha2WaiterResolve');
          if (typeof resolve === 'function') resolve(token);
        },
        'error-callback': () => {
          const reject = SDK.Tools.getOption('recaptcha2WaiterReject');
          if (typeof reject === 'function') reject(new Error('Recaptcha version 2 error occurred'));
        },
        'expired-callback': () => {
          const reject = SDK.Tools.getOption('recaptcha2WaiterReject');
          if (typeof reject === 'function') reject(new Error('Recaptcha version 2 token expired'));
        },
      });

      SDK.Tools.setOption('recaptcha2Sitekey', sitekey);
      SDK.Tools.setOption('recaptcha2WidgetId', widgetId);
    };

    enabled && sitekey && initiateRecaptcha();
  }, []);

  return enabled && sitekey ? <div id="recaptcha-2-container" ref={containerRef} /> : null;
}

async function loadExternalScripts(scripts) {
  const results = await Promise.allSettled(scripts.map((script) => attachScript(script)));
  results.forEach((result) => {
    if (result.status === 'rejected') {
      console.error(result.reason);
    }
  });
}

function attachScript(externalScript) {
  const { src, crossOrigin, async = false, defer = true, type = 'text/javascript' } = externalScript;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = type;
    script.async = async;
    script.defer = defer;
    script.src = src;

    // TODO: temporary
    if (crossOrigin) {
      script.crossOrigin = crossOrigin;
    }

    const wasScriptWasAlreadyAdded = !!document.querySelectorAll(`script[src='${src}']`).length;

    script.onload = function () {
      resolve({ script });
    };

    script.onerror = function (error) {
      console.error('error:', error);
      reject(error);
    };

    if (!wasScriptWasAlreadyAdded) {
      document.getElementsByTagName('head')[0].appendChild(script);
    } else {
      resolve();
    }
  });
}

/**
 * Returns a click handler, that checks if an anchor element is clicked wether it should function as a link or not
 * If openLinksInIframe is enabled and the element has data-target=iframe -> it functions as a button opening a dialog with iframe
 * If the app is opened inside webview and openLinksOutsideWebview is enabled -> it sends an OPEN_URL event to NGsoft
 * @param {Function} modalHandler The function that shows and hides the modal
 * @param {Function} emitEvent The function that emits events
 * @param {Object} linkBehaviorOverrides An object with the enabled overrides
 * @param {Boolean} isWebview A boolean indicating if the app is running in webview
 * @returns {Function} The click handler
 */
function handleLinkClicks(modalHandler, emitEvent, linkBehaviorOverrides, isWebview) {
  return function (evt) {
    if (evt.target.tagName !== 'A') return;

    const { openLinksInIframe, openLinksOutsideWebview } = linkBehaviorOverrides;

    if (openLinksInIframe && evt.target.getAttribute('data-target') === 'iframe') {
      evt.preventDefault();

      modalHandler({
        modalName: 'GlobalIframe',
        props: {
          open: true,
          onClose: modalHandler,
          src: evt.target.href,
          title: evt.target.getAttribute('data-title'),
        },
      });
    } else if (isWebview && openLinksOutsideWebview) {
      evt.preventDefault();

      emitEvent(SYSTEM_EVENTS.OPEN_URL, { url: evt.target.href });
    }
  };
}

UIFramework.propTypes = {
  demoMode: PropTypes.bool,
  mode: PropTypes.oneOf(Object.values(UI_FW_MODES)),
  config: PropTypes.object,
  children: PropTypes.node,
};

export const MainContext = React.createContext({});
export default UIFramework;
